import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useUserProfile } from '../hooks/useUserProfile';

export interface ChildProfile {
  id: number;
  parentId: string;
  name: string;
  birthDate: string | null;
  avatarHeroId: number | null;
  backgroundGradient: string;
  backgroundIntensity: string;
  createdAt: string;
  updatedAt: string;
}

interface ChildProfileContextType {
  children: ChildProfile[];
  activeChild: ChildProfile | null;
  loading: boolean;
  error: string | null;
  setActiveChild: (child: ChildProfile | null) => void;
  createChild: (name: string, birthDate?: string) => Promise<ChildProfile>;
  updateChild: (childId: number, updates: Partial<Pick<ChildProfile, 'name' | 'birthDate' | 'avatarHeroId'>>) => Promise<void>;
  deleteChild: (childId: number) => Promise<void>;
  refreshChildren: () => Promise<void>;
  canAddMoreChildren: boolean;
}

const ChildProfileContext = createContext<ChildProfileContextType | null>(null);

const MAX_CHILDREN = 3;

// Convert snake_case to camelCase for a single object
const toCamelCase = (obj: any): any => {
  if (!obj) return obj;
  const newObj: any = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    newObj[camelKey] = obj[key];
  }
  return newObj;
};

// Convert camelCase to snake_case for a single object
const toSnakeCase = (obj: any): any => {
  if (!obj) return obj;
  const newObj: any = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    newObj[snakeKey] = obj[key];
  }
  return newObj;
};

export const ChildProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useUserProfile();
  const [childProfiles, setChildProfiles] = useState<ChildProfile[]>([]);
  const [activeChild, setActiveChildState] = useState<ChildProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load children from Supabase (with optional auto-select of active child)
  const loadChildren = useCallback(async (autoSelectActive: boolean = true) => {
    if (!user) {
      setChildProfiles([]);
      setActiveChildState(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('child_profiles')
        .select('*')
        .eq('parent_id', user.id)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      const profiles = (data || []).map(toCamelCase) as ChildProfile[];
      setChildProfiles(profiles);

      // Only restore active child on initial load if explicitly stored
      // Do NOT auto-select first child - let user choose from ChildProfileSelector
      if (autoSelectActive) {
        const storedChildId = localStorage.getItem(`lilhero_active_child_${user.id}`);
        if (storedChildId) {
          const storedChild = profiles.find(c => c.id === parseInt(storedChildId));
          if (storedChild) {
            setActiveChildState(storedChild);
          }
          // If stored child no longer exists, don't auto-select - show selector
        }
        // If no stored child, don't auto-select - show selector
      }
    } catch (err: any) {
      console.error('Error loading child profiles:', err);
      setError(err.message || 'Erreur de chargement des profils');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load children when user changes
  useEffect(() => {
    if (isAuthenticated) {
      loadChildren();
    } else {
      setChildProfiles([]);
      setActiveChildState(null);
      setLoading(false);
    }
  }, [isAuthenticated, loadChildren]);

  // Clear active child when app is closed/hidden (return to profile selector on reopen)
  useEffect(() => {
    if (!user) return;

    const clearActiveChildOnHide = () => {
      // Clear localStorage so next visit shows profile selector
      localStorage.removeItem(`lilhero_active_child_${user.id}`);
    };

    // pagehide is more reliable than beforeunload on mobile
    window.addEventListener('pagehide', clearActiveChildOnHide);

    // Also handle visibility change for mobile app backgrounding
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearActiveChildOnHide();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', clearActiveChildOnHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  // Set active child and persist to localStorage
  const setActiveChild = useCallback((child: ChildProfile | null) => {
    setActiveChildState(child);
    if (user && child) {
      localStorage.setItem(`lilhero_active_child_${user.id}`, child.id.toString());
    } else if (user) {
      localStorage.removeItem(`lilhero_active_child_${user.id}`);
      // Refresh children when returning to selector (to get updated avatars from DB trigger)
      // Pass false to NOT auto-select an active child
      loadChildren(false);
    }
  }, [user, loadChildren]);

  // Create new child profile
  const createChild = useCallback(async (name: string, birthDate?: string): Promise<ChildProfile> => {
    if (!user) throw new Error('Non authentifié');
    if (childProfiles.length >= MAX_CHILDREN) {
      throw new Error(`Maximum ${MAX_CHILDREN} profils enfants par compte`);
    }

    // Get first available hero as default avatar
    let defaultHeroId: number | null = null;
    try {
      const { data: heroes } = await supabase
        .from('elements')
        .select('id')
        .eq('type', 'hero')
        .eq('unlocked_by_default', true)
        .limit(1);

      if (heroes && heroes.length > 0) {
        defaultHeroId = heroes[0].id;
      }
    } catch (err) {
      console.warn('Could not fetch default hero for avatar:', err);
    }

    const { data, error: insertError } = await supabase
      .from('child_profiles')
      .insert(toSnakeCase({
        parentId: user.id,
        name,
        birthDate: birthDate || null,
        avatarHeroId: defaultHeroId,
      }))
      .select()
      .single();

    if (insertError) throw insertError;

    const newChild = toCamelCase(data) as ChildProfile;
    setChildProfiles(prev => [...prev, newChild]);

    // If this is the first child, set as active
    if (childProfiles.length === 0) {
      setActiveChild(newChild);
    }

    return newChild;
  }, [user, childProfiles.length, setActiveChild]);

  // Update child profile
  const updateChild = useCallback(async (
    childId: number,
    updates: Partial<Pick<ChildProfile, 'name' | 'birthDate' | 'avatarHeroId'>>
  ): Promise<void> => {
    if (!user) throw new Error('Non authentifié');

    const { error: updateError } = await supabase
      .from('child_profiles')
      .update(toSnakeCase(updates))
      .eq('id', childId)
      .eq('parent_id', user.id);

    if (updateError) throw updateError;

    // Update local state
    setChildProfiles(prev => prev.map(c =>
      c.id === childId ? { ...c, ...updates } : c
    ));

    // Update active child if it's the one being updated
    if (activeChild?.id === childId) {
      setActiveChildState(prev => prev ? { ...prev, ...updates } : null);
    }
  }, [user, activeChild]);

  // Delete child profile
  const deleteChild = useCallback(async (childId: number): Promise<void> => {
    if (!user) throw new Error('Non authentifié');

    const { error: deleteError } = await supabase
      .from('child_profiles')
      .delete()
      .eq('id', childId)
      .eq('parent_id', user.id);

    if (deleteError) throw deleteError;

    // Update local state
    const newChildren = childProfiles.filter(c => c.id !== childId);
    setChildProfiles(newChildren);

    // If active child was deleted, switch to first remaining or null
    if (activeChild?.id === childId) {
      setActiveChild(newChildren[0] || null);
    }
  }, [user, childProfiles, activeChild, setActiveChild]);

  const value: ChildProfileContextType = {
    children: childProfiles,
    activeChild,
    loading,
    error,
    setActiveChild,
    createChild,
    updateChild,
    deleteChild,
    refreshChildren: loadChildren,
    canAddMoreChildren: childProfiles.length < MAX_CHILDREN,
  };

  return (
    <ChildProfileContext.Provider value={value}>
      {children}
    </ChildProfileContext.Provider>
  );
};

export const useChildProfile = (): ChildProfileContextType => {
  const context = useContext(ChildProfileContext);
  if (!context) {
    throw new Error('useChildProfile must be used within a ChildProfileProvider');
  }
  return context;
};

export default ChildProfileContext;
